<script setup lang="ts">
import type {
  ProductDetail,
  Surface,
  SurfaceCollection,
  SurfaceCollectionCategory,
  SurfaceType,
} from '~/types/api'

// TODO: make setNid required (not undefined) once /[line]/[slug] page has skeletons
const props = defineProps<{
  setNid?: number
  products: ProductDetail[]
  selectedProduct: ProductDetail
}>()

const emit = defineEmits(['update:selected-product'])

const surfacesRepository = createSurfacesRepository()

// load surface categories for set
const { data: surfaceCollectionCategories } = await useAsyncData(
  'set_surface_categories',
  () => surfacesRepository.getSetsPublicSurfaceCategories(props.setNid),
  {
    transform: (data) => data.items,
  }
)

const surfacesCollections = ref<{
  [key: number]: SurfaceCollection[]
}>({})

// load surface collections for each category and map them together
const collectionPromises = surfaceCollectionCategories.value?.map(
  (category: SurfaceCollectionCategory) => {
    return useAsyncData('product_surfaces_' + category.nid, () =>
      surfacesRepository.getSetsPublicSurfaceCollectionsByCategory(
        props.setNid,
        category.nid
      )
    )
  }
)
const surfaceCollectionValues = await Promise.allSettled(
  collectionPromises ?? []
)
surfaceCollectionCategories.value?.map(
  (category: SurfaceCollectionCategory, index) => {
    const val = surfaceCollectionValues[index]

    if (isFulfilled(val)) {
      const filteredCollection = val.value?.data.value?.items.filter(
        (collection) => collection.surfaceType?.isFabricOrMesh !== 0
      )
      if (filteredCollection.length) {
        surfacesCollections.value[category.nid] = filteredCollection
      }
    }
  }
)

// filter out categories with no collections
surfaceCollectionCategories.value =
  surfaceCollectionCategories.value?.filter(
    (category) => surfacesCollections.value[category.nid]
  ) ?? []

const selectedCollections = computed(() => {
  if (selectedCategory.value) {
    return surfacesCollections.value[selectedCategory.value.nid] ?? []
  } else {
    return []
  }
})

const selectedCategory = ref<SurfaceCollectionCategory | null>(null)
if (surfaceCollectionCategories.value?.length) {
  selectedCategory.value = surfaceCollectionCategories.value[0]
}
const selectedCollection = ref<SurfaceCollection | null>(null)

type TypeWithFabrics = {
  [key: number]: Surface[]
}
const surfaceTypeSurfaces = ref<TypeWithFabrics>({} as TypeWithFabrics)

// load all non fabric and non mesh types
const { data: nonRegularSurfaceTypes } = await useCachedAsyncData(
  'surface_types',
  () => surfacesRepository.getNonFabricAndMeshSurfaceTypes(),
  {
    transform: (data) => data.items,
  }
)

// load set surfaces by type and map them together
const typesPromises = nonRegularSurfaceTypes.value?.map((type: SurfaceType) => {
  return useAsyncData('set_non_fabrics_surfaces-' + type.nid, () =>
    surfacesRepository.getSetSurfacesByType(props.setNid, type.nid)
  )
})
const typesValues = await Promise.allSettled(typesPromises ?? [])

nonRegularSurfaceTypes.value?.forEach((type: SurfaceType, index) => {
  const val = typesValues[index]

  if (isFulfilled(val)) {
    surfaceTypeSurfaces.value[type.nid] = val.value?.data.value?.items ?? []
  }
})

// filter empty surface types
const filteredNonRegularSurfaceTypes =
  nonRegularSurfaceTypes.value?.filter(
    (type) => surfaceTypeSurfaces.value[type.nid || 0]?.length
  ) ?? []

const selectedType = ref<SurfaceType | null>(
  filteredNonRegularSurfaceTypes[0] ?? null
)
const activeSurfaces = computed(() => {
  if (selectedType.value === null) {
    return null
  }
  return surfaceTypeSurfaces.value[selectedType.value.nid]
})

watch(
  () => selectedCollections.value,
  () => {
    selectedCollection.value = selectedCollections.value[0] ?? []
  },
  { immediate: true }
)

// TODO: surface finishes missing surface preview
</script>

<template>
  <UiSection v-if="surfaceCollectionCategories?.length || selectedType">
    <template #title>
      <ProductTitleWithProductSelector
        :hide-selector="true"
        title="base.products.surfaces.fabricsAndSurfaces"
        :products="products"
        :selected-product="selectedProduct"
        :dark="true"
        @update:selected-product="
          (product) => emit('update:selected-product', product)
        "
      />
    </template>
    <TypographyBase class="w-full pb-10 lg:w-7/12">
      {{ $t('base.products.surfaces.text') }}
    </TypographyBase>
    <template v-if="surfaceCollectionCategories?.length">
      <div class="text- font-pragmatica-ext pb-5 text-2xl font-bold">
        {{ $t('base.products.surfaces.title') }}
      </div>
      <ProductSurfacesCarousel>
        <UiCarouselItem
          v-for="category in surfaceCollectionCategories"
          :key="category.nid"
          class="basis-auto"
        >
          <button
            class="flex w-fit flex-col items-center whitespace-nowrap px-5 py-3 hover:text-neutral-400"
            :class="{
              'bg-neutral-100': category.nid === selectedCategory?.nid,
            }"
            @click="selectedCategory = category"
          >
            <div class="text-xl font-bold">
              {{ $t('base.products.surfaces.category') + ' ' + category.title }}
            </div>
          </button>
        </UiCarouselItem>
      </ProductSurfacesCarousel>
      <ProductSurfacesCarousel>
        <UiCarouselItem
          v-for="collection in selectedCollections"
          :key="collection.nid"
          class="basis-auto"
        >
          <button
            class="flex w-fit flex-col items-center whitespace-nowrap px-5 py-3 hover:text-neutral-400"
            :class="{
              'bg-neutral-100': collection.nid === selectedCollection?.nid,
            }"
            @click="selectedCollection = collection"
          >
            <div class="text-lg font-bold">
              {{ collection.title }}
            </div>
          </button>
        </UiCarouselItem>
      </ProductSurfacesCarousel>
      <ProductSurfacesCollectionCard :collection="selectedCollection" />
    </template>
    <template v-if="selectedType">
      <div class="mt-10">
        <div class="font-pragmatica-ext pb-5 text-2xl font-bold">
          {{ $t('base.products.surfaces.surfacesTitle') }}
        </div>
        <ProductSurfacesCarousel>
          <UiCarouselItem
            v-for="type in filteredNonRegularSurfaceTypes"
            :key="type.nid"
            class="basis-auto"
          >
            <button
              class="flex w-fit flex-col items-center whitespace-nowrap px-5 py-3 hover:text-neutral-400"
              :class="{
                'bg-neutral-100': type.nid === selectedType.nid,
              }"
              @click="selectedType = type"
            >
              <div class="text-lg font-bold">
                {{ type.title }}
              </div>
            </button>
          </UiCarouselItem>
        </ProductSurfacesCarousel>
        <!-- TODO: could be ProductSurfacesGrid component -->
        <div
          class="grid w-full grid-cols-3 gap-4 border-b-4 border-neutral-100 pb-8 pt-8 min-[528px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12"
        >
          <div v-for="surface in activeSurfaces" :key="surface.nid">
            <NuxtImg
              class="mb-2 aspect-square w-full"
              sizes="150px"
              loading="lazy"
              :src="withHost(surface.image?.url)"
              :alt="surface.image?.metaAlt || undefined"
              :title="surface.image?.metaTitle || undefined"
            />
            <UTooltip
              :text="surface.title"
              :popper="{ placement: 'top' }"
              :ui="{
                wrapper: 'relative inline',
                color: 'text-white',
                background: 'bg-black',
                rounded: 'rounded-none',
                base: '[@media(pointer:coarse)]:hidden h-6 px-3 py-3 text-sm font-normal break-normal whitespace-normal h-fit after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-black',
              }"
            >
              <div
                class="font-pragmatica-ext truncate text-center text-sm font-bold"
              >
                {{ surface.title }}
              </div>
            </UTooltip>
          </div>
        </div>
      </div>
    </template>
  </UiSection>
</template>
